import { zodResolver } from '@hookform/resolvers/zod';
import pick from 'lodash/pick';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { deepDifference } from '@/helpers';
import { useLeavePageGuard } from '@/hooks/useUnsavedChangesPrompt';

import { useEventDetails } from '../api';
import { EventDetailsTab } from '../constants';
import { getDefaultValues, getModelRequest } from '../helpers';
import { EventFormSchemaType } from '../pages';
import { UpdateEventPayload } from '../types';
import { EventFormSchema } from '../validations';

export const useEventDetailsHandler = () => {
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const {
    eventQuery: { data: details, isFetching, error, refetch: refetchEvent },
    saveMediaMutation,
    updateEventMutation,
    isUpdateLoading,
  } = useEventDetails(params.id, params.tab);

  const defaultValues: EventFormSchemaType = useMemo(
    () => getDefaultValues(details),
    [details]
  );

  const form = useForm<EventFormSchemaType>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: defaultValues,
    values: defaultValues,
    disabled: isFetching,
    mode: 'onChange',
  });

  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length === 0;

  const { isModalOpen, confirmLeave, cancelLeave } =
    useLeavePageGuard(!hasDirtyFields);

  const updateEvent = async (data, allowedFields?: string[]) => {
    try {
      const currentCategoryId = form.getValues('systemPromptCategoryId');

      const body = deepDifference<UpdateEventPayload>(data, defaultValues);

      const filteredBody = allowedFields ? pick(body, allowedFields) : body;

      const payload = {
        ...filteredBody,
        ...(!allowedFields || allowedFields.includes('masks')
          ? {
              masks: data.masks.map((mask) => ({
                maskId: mask.maskId,
                overlayId: mask.overlayId || null,
              })),
            }
          : {}),
        ...(!allowedFields || allowedFields.includes('types')
          ? { types: data.types.map((type: string) => ({ typeId: type })) }
          : {}),
        ...(!allowedFields || allowedFields.includes('styles') ? { styles: data.styles } : {}),
        ...(!allowedFields || allowedFields.includes('textStyles') ? { textStyles: data.textStyles } : {}),
        ...(!allowedFields || allowedFields.includes('models') ? { models: data.models } : {}),
        ...(!allowedFields || allowedFields.includes('printers') ? { printers: data.printers } : {}),
        ...(!allowedFields || allowedFields.includes('lacesColors') ? { lacesColors: data.lacesColors } : {}),
        ...(!allowedFields || allowedFields.includes('collarColors') ? { collarColors: data.collarColors } : {}),
        ...(data.systemPromptCategoryId && data.systemPromptId
          ? {
              systemPromptId: data.systemPromptId,
              systemPromptContent: data.systemPromptContent,
              surpriseMeContent: data.systemPromptSurpriseMeContent,
              systemPromptNotes: data.systemPromptNotes,
            }
          : {}),
        id: params.id,
      };

      const updatedEventData = await updateEventMutation.mutateAsync(payload);

      //reset form with updated data
      form.reset(getDefaultValues(updatedEventData));

      if (currentCategoryId) {
        form.setValue('systemPromptCategoryId', currentCategoryId, { shouldDirty: false });
      }

      toast.success(`Event “${data.name}” updated successfully!`);

      if (isModalOpen) {
        confirmLeave();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveMedia = async (values: EventFormSchemaType) => {
    const { dirtyFields } = form.formState;

    const updatedEventData = await saveMediaMutation.mutateAsync({
      video: { value: values.publicUrl, isDirty: !!dirtyFields.publicUrl },
      idleStateVideo: {
        value: values.idleStateVideoPublicUrl,
        isDirty: !!dirtyFields.idleStateVideoPublicUrl,
      },
      backgroundImage: {
        value: values.backgroundImageUrl,
        isDirty: !!dirtyFields.backgroundImageUrl,
      },
    });

    // PATCH /media returns the updated event; when nothing changed it resolves
    // to null, so fall back to current values to clear the dirty state.
    form.reset(updatedEventData ? getDefaultValues(updatedEventData) : values);

    toast.success(`Event “${values.name}” updated successfully!`);

    if (isModalOpen) {
      confirmLeave();
    }
  };

  const onFormSubmit = async () => {
    const values = form.getValues();

    const tabActions = {
      [EventDetailsTab.Info]: async () => {
        return form.trigger(['name', 'types']);
      },

      [EventDetailsTab.AIConfiguration]: async () => {
        return form.trigger(['aiModel', 'systemPromptCategoryId']);
      },

      [EventDetailsTab.Video]: async () => {
        return form.trigger(['publicUrl']);
      },

      [EventDetailsTab.Models]: async () => {
        return form.trigger(['models']);
      },

      [EventDetailsTab.Styles]: async () => {
        return form.trigger(['styles', 'textStyles']);
      },

      [EventDetailsTab.Masks]: async () => {
        return form.trigger(['masks']);
      },

      [EventDetailsTab.LacesColors]: async () => {
        return form.trigger(['lacesColors']);
      },

      [EventDetailsTab.CollarColors]: async () => {
        return form.trigger(['collarColors']);
      },

      [EventDetailsTab.Printers]: async () => {
        return form.trigger(['printers']);
      },
    };

    try {
      const runValidation = tabActions[params.tab];

      const isValid = runValidation ? await runValidation() : true;

      if (!isValid) {
        toast.error('Please fill in all required fields.');
        return;
      }

      if (params.tab === EventDetailsTab.Video) {
        await saveMedia(values);
        return;
      }

      const tabAllowedFields: Partial<Record<EventDetailsTab, string[]>> = {
        [EventDetailsTab.Info]: ['name', 'startDate', 'status', 'remixingEnabled', 'groupOrderingEnabled', 'isGeneratedImageCodeEnabled', 'isPromoSmsEnabled', 'surpriseMeEnabled', 'rectangleImageGenerationEnabled', 'isFadedHeelEnabled', 'isShoeSizeHintEnabled', 'types'],
        [EventDetailsTab.Styles]: ['styles', 'textStyles'],
      };

      await updateEvent(
        { ...values, models: getModelRequest(values.models) },
        tabAllowedFields[params.tab],
      );
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onDiscardChanges = (isLeave = true) => {
    if (isLeave) confirmLeave();

    form.reset(defaultValues);
    form.trigger();
  };

  useEffect(() => {
    if (!defaultValues) return;

    const aiErrors = details?.validationErrors?.aiModel ?? [];

    form.trigger().then(() => {
      if (aiErrors.includes('systemPrompt')) {
        form.setError('systemPromptCategoryId', { type: 'server', message: 'System prompt is required' });
      }
    });
  }, [defaultValues]);

  return {
    details,
    isLoading: isFetching,
    error,
    form,
    refetchEvent,
    onFormSubmit,
    onReset: () => onDiscardChanges(false),
    isUpdateLoading,
    disabledSaveButton: hasDirtyFields,
    unsavedChangesModalProps: {
      isOpen: isModalOpen,
      onOpenChange: cancelLeave,
      onSubmit: onDiscardChanges,
      isLoading: updateEventMutation.isPending,
    },
  };
};
